/**
 * Which first activity somebody gets after the intro (well/product/33).
 *
 * Day 1's first activity has always been the warm-up: tell a story out loud.
 * Inside Facebook's and Instagram's in-app browsers, where every paid visitor
 * lands, nobody who reached it got past it (2026-10-14). An alternative starter
 * that needs no microphone sits beside it, and an ad picks it with a url
 * parameter: `?start=imagine`.
 *
 * Like the door, it is a fact about somebody's *first* arrival, so it is read
 * once off the first landing and kept in the first-touch campaign record
 * (lib/campaign.ts), where a reload, a tap and a return all find it. An unknown
 * value is no starter at all: the default stays the default until the owner
 * says otherwise.
 */
import { loadCampaign } from './campaign';

export const STARTERS = ['imagine'] as const;
export type Starter = (typeof STARTERS)[number];

export function isStarter(x: unknown): x is Starter {
  return typeof x === 'string' && (STARTERS as readonly string[]).includes(x);
}

/** `?start=imagine` on the url, or nothing. */
export function starterFrom(url: URL): Starter | undefined {
  const s = url.searchParams.get('start')?.trim().toLowerCase();
  return isStarter(s) ? s : undefined;
}

/** The starter this person arrived with, if any. */
export function currentStarter(): Starter | undefined {
  const s = loadCampaign()?.start;
  return isStarter(s) ? s : undefined;
}

/** Where "Start exercise 1" goes: the warm-up, unless the ad said otherwise. */
export function startPath(starter: Starter | undefined = currentStarter()): string {
  return starter === 'imagine' ? '/imagine' : '/start';
}
